import React from 'react';
import { Box, Typography, Card, CardContent, capitalize } from '@mui/material';
import { DirectionsCar, Home, Diamond, Category } from '@mui/icons-material';
import { camposGarantia } from '../../../../utils/staticvariables';

/**
 * Sistema visual compartido del módulo de garantías (estilo sobrio, alineado con
 * la pantalla de detalle del préstamo). Lo usan tanto la pantalla independiente
 * (index.js) como el tab dentro del detalle del préstamo (GarantiasPrestamo.js).
 */

// --- Estado (ciclo de vida) ---
export const ESTADO_META = {
  activa:     { label: 'Activa',     dot: '#0288d1', bg: 'rgba(2,136,209,0.10)',  text: '#0277bd' },
  liberada:   { label: 'Liberada',   dot: '#2e7d32', bg: 'rgba(46,125,50,0.10)',  text: '#2e7d32' },
  ejecutada:  { label: 'Ejecutada',  dot: '#c62828', bg: 'rgba(198,40,40,0.10)',  text: '#c62828' },
  sustituida: { label: 'Sustituida', dot: '#757575', bg: 'rgba(117,117,117,0.12)', text: '#616161' },
};

export function EstadoChip({ estado }) {
  const meta = ESTADO_META[estado] || ESTADO_META.activa;
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1,
        py: 0.35,
        borderRadius: 1,
        bgcolor: meta.bg,
      }}
    >
      <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: meta.dot, flexShrink: 0 }} />
      <Typography variant="caption" sx={{ fontWeight: 700, color: meta.text, lineHeight: 1, letterSpacing: '0.02em' }}>
        {meta.label}
      </Typography>
    </Box>
  );
}

// --- Categoría ---
export const CATEGORIA_META = {
  vehiculo: { label: 'Vehículo', icon: DirectionsCar, color: '#1565c0' },
  inmueble: { label: 'Inmueble', icon: Home,          color: '#2e7d32' },
  prendas:  { label: 'Prendas',  icon: Diamond,       color: '#7b1fa2' },
  otro:     { label: 'Otro',     icon: Category,      color: '#616161' },
};

export function CategoryChip({ categoria }) {
  const meta = CATEGORIA_META[categoria] || CATEGORIA_META.otro;
  const Icon = meta.icon;
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.85 }}>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 26,
          height: 26,
          borderRadius: 1,
          bgcolor: `${meta.color}14`,
          color: meta.color,
        }}
      >
        <Icon sx={{ fontSize: 16 }} />
      </Box>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>{meta.label}</Typography>
    </Box>
  );
}

// --- Encabezado de sección (barra lateral) ---
export function SectionHeader({ title, action }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 2.5,
        pb: 1.25,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box sx={{ width: 3, height: 16, borderRadius: 1, bgcolor: 'primary.main' }} />
        <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.85rem', letterSpacing: '0.01em' }}>
          {title}
        </Typography>
      </Box>
      {action}
    </Box>
  );
}

// --- Tarjeta de estadística (sobria, outlined) ---
export function StatCard({ label, value, accent = '#18586b', active = false, onClick }) {
  return (
    <Card
      variant="outlined"
      onClick={onClick}
      sx={{
        borderRadius: 2,
        boxShadow: 'none',
        cursor: onClick ? 'pointer' : 'default',
        borderColor: active ? accent : 'divider',
        borderWidth: active ? 1.5 : 1,
        transition: 'border-color 0.15s ease',
        '&:hover': onClick ? { borderColor: accent } : undefined,
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: accent }} />
          <Typography
            variant="caption"
            sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.secondary' }}
          >
            {label}
          </Typography>
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 700, color: accent, lineHeight: 1 }}>
          {value}
        </Typography>
      </CardContent>
    </Card>
  );
}

// --- Helpers de datos ---
export const parseGarantia = (g) => {
  if (!g) return {};
  try {
    return typeof g.garantia === 'string' ? JSON.parse(g.garantia) : (g.garantia || {});
  } catch {
    return {};
  }
};

export const garantiaLabel = (g) => {
  if (!g) return 'N/A';
  const d = parseGarantia(g);
  const detail = d.id || d.marca || d.descrip || g.id?.substring(0, 8);
  return `${capitalize(g.categoria)} — ${detail}`;
};

export const camposDeCategoria = (categoria) =>
  camposGarantia.find((c) => c.value === categoria)?.campos || [];
